import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { RelativeUsageOptions, UsageCache, UsageOptions } from "./usage.cache";

/** Cache of resource usages over time */
export class ResourceUsageCache<Resource, Data = void> {

    protected readonly _DEFAULT_QUANTITY = 1;
    protected readonly _timelines: Map<Resource, UsageCache<Data>> = new Map();

    constructor(protected _options?: {
        availableQuantities?: Map<Resource, number>
    }) {
        if (this._options?.availableQuantities) {
            for (const [resource, quantity] of this._options.availableQuantities.entries()) {
                this.getResourceTimeline(resource, quantity);
            }
        }
    }

    //#region Add / Delete ----------------------------------------------------

    /** Add all relative resources usages based on start instant */
    public useAll(instant: UnixTimestamp, resourceUsages: Map<Resource, RelativeUsageOptions<Data>[]>): void {
        for (const [resource, usages] of resourceUsages.entries()) {
            this.getResourceTimeline(resource).useAll(instant, usages);
        }
    }

    /** Add all relative resources usages based on start instant */
    public releaseAll(instant: UnixTimestamp, resourceUsages: Map<Resource, RelativeUsageOptions<Data>[]>): void {
        for (const [resource, usages] of resourceUsages.entries()) {
            this.getResourceTimeline(resource).releaseAll(instant, usages);
        }
    }

    /**
     * Add a quantity of usage at given start instant and remove it at finish instant.
     * If start or finish are not provided, the quantity is added/subtracted all over the time.
     */
    public use(resource: Resource, quantityToUse: number, options: UsageOptions<Data>): void {
        this.getResourceTimeline(resource).use(quantityToUse, options);
    }

    /** Subtract a quantity from resource on the given time period (add -quantity) */
    public release(resource: Resource, quantityToRelease: number, options: UsageOptions<Data>): void {
        this.getResourceTimeline(resource).release(quantityToRelease, options);
    }

    //#endregion

    //#region Compute quantities cache ----------------------------------------

    /** Remove duplicate entries */
    public pack(): void {
        for (const [resource, timeline] of this._timelines.entries()) {
            timeline.pack();
        }
    }

    /** 
     * Force a recompute of the quantities cache for all resources.
     * Use this function after multiple add/sub with skipRecomputeQuantities 
     */
    public recompute(): void {
        for (const [resource, timeline] of this._timelines.entries()) {
            timeline.recompute();
        }
    }

    //#endregion

    //#region State management ------------------------------------------------

    /** 
     * Clone the resources usages to test something on the timeline.
     * A list of resources can be be passed to avoid cloning all the timelines.
     */
    public clone(limitToResources?: Iterable<Resource>): ResourceUsageCache<Resource, Data> {
        const clone: ResourceUsageCache<Resource, Data> = new ResourceUsageCache({
            ...this._options,
            // We don't care about available quantities as they will be cloned in timelines
            availableQuantities: undefined
        });
        for (const resource of limitToResources ?? this._timelines.keys()) {
            const usageCache = this._timelines.get(resource)?.clone() ?? new UsageCache(this._DEFAULT_QUANTITY);
            clone._timelines.set(resource, usageCache);
        }
        return clone;
    }

    //#endregion

    //#region Utility functions -----------------------------------------------

    /** @returns The used quantity at given instant and the period of validity of the function */
    public getResourceUsageAt(resource: Resource, instant: UnixTimestamp): { usage: number, from?: UnixTimestamp, to?: UnixTimestamp } {
        return this.getResourceTimeline(resource).getUsageAt(instant);
    }

    /** @returns The available quantity at given instant and the period of validity of the function */
    public getResourceAvailabilityAt(resource: Resource, instant: UnixTimestamp): { available: number, usage: number, from?: UnixTimestamp, to?: UnixTimestamp } {
        return this.getResourceTimeline(resource).getAvailabilityAt(instant);
    }

    // /** @returns true If usages can be fulfilled at a given instant */
    // public isAvailable(instant: UnixTimestamp, usages: Usage<Resource, Data>[]): boolean {
    //     return false;
    // }

    /** @returns Next instant where the resources will be available */
    public getNextAvailability(instant: UnixTimestamp, resourceUsages: Map<Resource, RelativeUsageOptions<Data>[]>, max?: UnixTimestamp): UnixTimestamp | null {
        let reference = instant;
        // When max is null, we loop until we find the next availability or we can't plan the usages
        // eslint-disable-next-line no-unmodified-loop-condition
        while (max == null || reference < max) {
            let nextReference = reference;
            for (const [resource, usages] of resourceUsages.entries()) {
                const nextAvailability = this.getResourceTimeline(resource).getNextAvailability(nextReference, usages);
                if (nextAvailability == null) {
                    // Resource will never be available in the future, no way to plan the usages
                    return null;
                } else if (nextReference < nextAvailability) {
                    // Post pone the planning of the usages
                    nextReference = nextAvailability;
                }
            }
            // Reference was not updated and 
            if (reference === nextReference) {
                // All resources were available at given reference
                return reference;
            } else {
                // We test the next reference
                reference = nextReference;
            }
        }
        return reference;
    }

    //#endregion

    //#region Timeline tools --------------------------------------------------

    /** Get or create a timeline for the given resource */
    public getResourceTimeline(resource: Resource, availableQuantity?: number): UsageCache<Data> {
        let result = this._timelines.get(resource);
        if (result == null) {
            result = new UsageCache(availableQuantity ?? this._DEFAULT_QUANTITY);
            this._timelines.set(resource, result);
        }
        return result;
    }

    /** Get the list of timelines */
    public getExistingTimelines(): [Resource, UsageCache<Data>][] {
        return [...this._timelines.entries()];
    }

    //#endregion

    //#region Debug -----------------------------------------------------------

    /** Get a debug string of all the existing timelines */
    public debug(dn: (resource: Resource) => string): string {
        let result = "";
        for (const [resource, timeline] of this.getExistingTimelines()) {
            result += `Resource: ${dn(resource)}, quantity: ${timeline.quantity}\n`;
            result += timeline.debug();
            result += "\n";
        }
        return result;
    }

    /** @throws If consistency is not correct */
    public checkConsistency(): void {
        for (const [_, timeline] of this.getExistingTimelines()) {
            timeline.checkConsistency();
        }
    }

    //#endregion
}